import { useEffect, useState } from "react";
import { CreditCard, AlertTriangle, Calendar, Users, Church, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useSubscription } from "@/hooks/useSubscription";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

type StripeInvoice = {
  id: string;
  created: number;
  amount_paid: number;
  currency: string;
  status: string;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
};

export default function Billing() {
  const {
    subscription, isActive,
    memberCount, memberLimit, isAtMemberLimit,
    openBillingPortal, loading,
  } = useSubscription();
  const { isChurchAdmin } = usePermissions();
  const { loading: authLoading } = useAuth();
  const { profile } = useChurch();
  const queryClient = useQueryClient();
  // Only church admins can manage billing; invited staff see a read-only view
  const canManageBilling = isChurchAdmin;
  const hasChurchProfile = !!profile?.id;

  // Show success toast when returning from Stripe Checkout
  useEffect(() => {
    const syncAfterStripeReturn = async () => {
      if (!profile?.id) return;

      const params = new URLSearchParams(window.location.search);
      const isCheckoutReturn = params.get("checkout") === "success";
      const isPortalReturn = params.get("portal") === "return";
      if (!isCheckoutReturn && !isPortalReturn) return;

      const sessionId = params.get("session_id");
      const { data, error } = await supabase.functions.invoke("stripe-sync-subscription", {
        body: {
          church_id: profile.id,
          session_id: sessionId,
        },
      });

      if (error || data?.error) {
        toast.error(data?.error || "Subscription sync failed. Please refresh.");
      } else {
        await queryClient.invalidateQueries({ queryKey: ["subscription", profile.id] });
        if (isCheckoutReturn) {
          toast.success("Subscription activated! Welcome aboard.");
        }
      }

      window.history.replaceState({}, document.title, window.location.pathname);
    };

    syncAfterStripeReturn();
  }, [profile?.id]);
  const memberUsagePercent = memberLimit ? Math.min(100, (memberCount / memberLimit) * 100) : 0;
  const [invoicePage, setInvoicePage] = useState(0);
  const invoicesPerPage = 10;

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery({
    queryKey: ["billing_invoices", profile?.id],
    enabled: !!profile?.id,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("stripe-list-invoices", {
        body: { church_id: profile!.id },
      });

      if (error || data?.error) {
        throw new Error(data?.error || error?.message || "Unable to load billing history");
      }

      return (data?.invoices ?? []) as StripeInvoice[];
    },
  });

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-pulse text-muted-foreground">Loading billing...</div>
      </div>
    );
  }

  const totalInvoicePages = Math.max(1, Math.ceil(invoices.length / invoicesPerPage));
  const invoiceStart = invoicePage * invoicesPerPage;
  const pagedInvoices = invoices.slice(invoiceStart, invoiceStart + invoicesPerPage);
  const canGoPrev = invoicePage > 0;
  const canGoNext = invoicePage < totalInvoicePages - 1;

  // Non-admin staff/member with no active subscription — they can't subscribe themselves
  if (!canManageBilling && !isActive) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4 max-w-md mx-auto">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center">
          <Church className="w-8 h-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-heading font-semibold text-foreground">Subscription Required</h2>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Your church doesn't have an active subscription yet. Please contact your church
          administrator to set up a billing plan so your team can access all features.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <CreditCard className="w-6 h-6 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Billing</h1>
          <p className="text-sm text-muted-foreground">Plans are based on the number of members your church manages</p>
        </div>
      </div>

      {/* Current subscription status with member usage */}
      <section className="border-y border-border py-6 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="space-y-3 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-foreground">Current Plan</h3>
              {subscription && subscription.status !== "active" && subscription.status !== "trialing" && subscription.status !== "canceled" && (
                <Badge
                  variant="secondary"
                  className={cn(subscription.status === "past_due" && "bg-destructive/10 text-destructive")}
                >
                  {subscription.status}
                </Badge>
              )}
            </div>

            {subscription ? (
              <>
                {/* Member usage bar */}
                <div className="space-y-2 w-full">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5 text-muted-foreground">
                      <Users className="w-4 h-4" />
                      Members Used
                    </span>
                    <span className={cn("font-medium", isAtMemberLimit ? "text-destructive" : "text-foreground")}> 
                      {memberCount} / {memberLimit?.toLocaleString() ?? "5,000"}
                    </span>
                  </div>
                  {memberLimit && (
                    <Progress
                      value={memberUsagePercent}
                      className={cn("h-2", memberUsagePercent >= 90 && "[&>div]:bg-destructive")}
                    />
                  )}
                  {isAtMemberLimit && (
                    <p className="text-xs text-destructive font-medium">
                      You've reached your member limit. Upgrade to add more members.
                    </p>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {subscription.status === "canceled" ? "Ending" : "Next billing"}: {format(new Date(subscription.current_period_end), "MMM d, yyyy")}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                No active subscription yet. Choose a Monthly or Yearly plan below to activate billing.
              </p>
            )}
          </div>
        </div>
      </section>

      {!hasChurchProfile && (
        <section className="py-6 border-b border-amber-300/60 bg-amber-50/70 space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
            <div>
              <p className="font-medium text-foreground">Still linking your church profile</p>
              <p className="text-sm text-muted-foreground">
                Please wait a moment and refresh this page. Billing starts as soon as your church profile is fully linked.
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Payment method */}
      {subscription && subscription.status !== "canceled" && (
        <section className="py-6 border-b border-border space-y-4">
          <div className="flex items-center gap-3 mb-4">
            <CreditCard className="w-5 h-5 text-primary" />
            <h3 className="font-semibold text-foreground">Payment Method</h3>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-7 rounded bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">
                VISA
              </div>
              <div className="text-sm">
                <p className="text-foreground font-medium">•••• •••• •••• 4242</p>
                <p className="text-muted-foreground">Expires 12/28</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={openBillingPortal}>
              Manage billing
            </Button>
          </div>
        </section>
      )}

      <section className="py-6 border-b border-border space-y-4">
        <div className="flex items-center justify-between gap-4">
          <h3 className="font-semibold text-foreground">Billing history</h3>
        </div>

        <div className="rounded-xl border border-border/70 overflow-hidden">
          {invoicesLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading receipts...</div>
          ) : invoices.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">No billing receipts yet.</div>
          ) : (
            <div className="divide-y divide-border/70">
              {pagedInvoices.map((invoice) => {
                const formattedAmount = new Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: (invoice.currency || "usd").toUpperCase(),
                }).format((invoice.amount_paid ?? 0) / 100);

                const paid = invoice.status === "paid";

                return (
                  <div key={invoice.id} className="grid grid-cols-[1.1fr_0.8fr_0.7fr_auto] items-center gap-2 px-4 py-3 text-sm">
                    <span className="text-foreground">
                      {format(new Date(invoice.created * 1000), "MMM d, yyyy")}
                    </span>
                    <span className="text-muted-foreground">{formattedAmount}</span>
                    <span>
                      <span className={cn(
                        "inline-flex rounded-md px-2 py-0.5 text-xs font-medium",
                        paid
                          ? "bg-emerald-100 text-emerald-700"
                          : "bg-muted text-muted-foreground"
                      )}>
                        {paid ? "Paid" : invoice.status}
                      </span>
                    </span>
                    <div className="flex items-center gap-3 justify-end">
                      {invoice.hosted_invoice_url ? (
                        <a
                          href={invoice.hosted_invoice_url}
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-foreground hover:text-primary"
                        >
                          View
                        </a>
                      ) : (
                        <span className="text-muted-foreground">View</span>
                      )}

                      {invoice.invoice_pdf ? (
                        <a
                          href={invoice.invoice_pdf}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 text-foreground hover:text-primary"
                          title="Download receipt"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {!invoicesLoading && invoices.length > 0 && (
          <div className="flex items-center justify-between pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoPrev}
              onClick={() => canGoPrev && setInvoicePage((p) => p - 1)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!canGoNext}
              onClick={() => canGoNext && setInvoicePage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </section>

    </div>
  );
}
